<template>
  <el-card
    shadow="hover"
    class="group-card group-card--interactive"
    :class="{ 'group-card--synced': isSynced }"
    @click="handleCardClick">
    <!-- Header: Group name + badge + actions -->
    <div class="group-card__header">
      <div class="group-card__header-content">
        <h3 class="group-card__title" :title="group.name">{{ group.name }}</h3>

        <!-- Sync badge if linked to data source -->
        <el-tag
          v-if="isSynced"
          size="small"
          type="info"
          effect="plain"
          class="group-card__badge">
          <span class="group-card__badge-content">
            <Link2 :size="12" />
            <span>{{ i18n.t('synced') }}</span>
          </span>
        </el-tag>
      </div>

      <!-- Header actions: visible on hover -->
      <div class="group-card__header-actions">
        <!-- Assign User icon button -->
        <SmTooltip
          v-if="!isSynced"
          :content="i18n.t('assignUser')"
          placement="top">
          <div
            class="group-card__menu-trigger"
            @click.stop="emit('addUser', group)">
            <UserPlus :size="16" />
          </div>
        </SmTooltip>

        <!-- Kebab menu for more actions -->
        <SmDropdown trigger="click" placement="bottom-end" @click.stop>
          <template #trigger>
            <div class="group-card__menu-trigger">
              <MoreVertical :size="16" />
            </div>
          </template>

          <SmDropdownItem :icon="SquarePen" @click="emit('edit', props.group)">
            {{ i18n.t('edit') }}
          </SmDropdownItem>

          <SmDropdownItem
            v-if="!isSynced"
            :icon="UserPlus"
            @click="emit('addUser', props.group)">
            {{ i18n.t('assignUser') }}
          </SmDropdownItem>

          <SmDropdownItem :icon="Eye" @click="handleView">
            {{ i18n.t('viewDetails') }}
          </SmDropdownItem>

          <SmDropdownItem
            :icon="Trash2"
            hover-color="var(--sm-magenta)"
            @click="emit('delete', props.group)">
            <span class="text-red-500">{{ i18n.t('delete') }}</span>
          </SmDropdownItem>
        </SmDropdown>
      </div>
    </div>

    <!-- Body: Stats row + apps row -->
    <div class="group-card__body">
      <!-- Stats: User count + License count -->
      <div class="group-card__stats">
        <div class="group-card__stat">
          <Users :size="14" class="text-contrast-muted" />
          <span class="group-card__stat-value">
            {{ group.accounts?.length ?? 0 }}
          </span>
          <span class="group-card__stat-label">
            {{
              (group.accounts?.length ?? 0) === 1
                ? i18n.t('user')
                : i18n.t('users')
            }}
          </span>
        </div>

        <!-- License count (calculated from assigned apps) -->
        <div v-if="licenseCount > 0" class="group-card__stat">
          <Key :size="14" class="text-contrast-muted" />
          <span class="group-card__stat-value">{{ licenseCount }}</span>
          <span class="group-card__stat-label">{{ i18n.t('licenses') }}</span>
        </div>
      </div>

      <!-- Application icons with overflow handling -->
      <div class="group-card__apps">
        <SmTooltip
          v-for="(app, index) in displayedApps"
          :key="app._id"
          :content="app.display_name || app.software_name"
          placement="top">
          <div
            class="group-card__app-icon"
            :style="getAppIconStyle(index)"
            @mouseenter="hoveredAppIndex = index"
            @mouseleave="hoveredAppIndex = null">
            <ApplicationIcon
              :size="18"
              :application-name="app.software_name"
              :application-type="app.type" />
          </div>
        </SmTooltip>

        <!-- Overflow indicator -->
        <div
          v-if="overflowCount > 0"
          class="group-card__app-overflow"
          :style="getOverflowStyle()">
          +{{ overflowCount }}
        </div>

        <!-- Empty state for no apps -->
        <div v-if="applications.length === 0" class="group-card__no-apps">
          <span class="text-sm text-contrast-muted">
            {{ i18n.t('noAppsAssigned') }}
          </span>
        </div>
      </div>
    </div>
  </el-card>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { computed, ref } from 'vue'
  import {
    saasmetrix__models__account__AccountGroup__AccountGroup as AccountGroup,
    SoftwareOut,
    SoftwarePermission_Output,
    SubAccountPermission_Output,
  } from '@/client'
  import { ApplicationStore } from '@/stores/application/application.store'
  import {
    MoreVertical,
    Eye,
    SquarePen,
    UserPlus,
    Users,
    Key,
    Link2,
    Trash2,
  } from 'lucide-vue-next'

  const i18n = useI18n()
  const allApplications = ApplicationStore.useApplications<SoftwareOut>().data

  const emit = defineEmits<{
    (e: 'edit', group: AccountGroup): void
    (e: 'addUser', group: AccountGroup): void
    (e: 'view', group: AccountGroup): void
    (e: 'delete', group: AccountGroup): void
  }>()

  const props = defineProps<{
    group: AccountGroup
  }>()

  // Computed properties
  const isSynced = computed(() => !!props.group.data_source?.data_source_id)

  const applicationIds = computed(() =>
    Object.keys(props.group.permission?.software_permissions || {})
  )

  const applications = computed<SoftwareOut[]>(() =>
    allApplications.value
      ? allApplications.value.filter((s) =>
          applicationIds.value.includes(s._id)
        )
      : []
  )

  const MAX_DISPLAY_APPS = 5
  const displayedApps = computed(() =>
    applications.value.slice(0, MAX_DISPLAY_APPS)
  )

  const overflowCount = computed(() =>
    Math.max(0, applications.value.length - MAX_DISPLAY_APPS)
  )

  const licenseCount = computed(() => {
    const permissions = props.group.permission?.software_permissions || {}
    return Object.values(permissions).reduce(
      (sum: number, perm: SoftwarePermission_Output) => {
        if (!perm.sub_account_permissions) return sum

        const appLicenseCount = Object.values(
          perm.sub_account_permissions
        ).reduce((subSum: number, subPerm: SubAccountPermission_Output) => {
          const licenses = subPerm.software_license_permissions || {}
          return subSum + Object.keys(licenses).length
        }, 0)

        return sum + appLicenseCount
      },
      0
    )
  })

  // Animation State
  const hoveredAppIndex = ref<number | null>(null)

  // Methods
  function handleCardClick() {
    handleView()
  }

  function handleView() {
    emit('view', props.group)
  }

  function getAppIconStyle(index: number) {
    const isHovered = hoveredAppIndex.value === index
    const isAfterHover =
      hoveredAppIndex.value !== null && index > hoveredAppIndex.value

    // Base overlap offset
    let offset = index * 12

    // If an item to the left is hovered, shift this item to the right
    if (isAfterHover) {
      offset -= 12 // Reduce overlap (move right)
    }

    return {
      transform: `translateX(-${offset}px) scale(${isHovered ? 1.15 : 1})`,
      zIndex: isHovered ? 100 : index + 1,
      transition: 'all 0.2s ease-in-out',
    }
  }

  function getOverflowStyle() {
    const isAfterHover = hoveredAppIndex.value !== null

    let offset = displayedApps.value.length * 12
    if (isAfterHover) {
      offset -= 12
    }

    return {
      transform: `translateX(-${offset}px)`,
      zIndex: displayedApps.value.length + 1,
      transition: 'all 0.2s ease-in-out',
    }
  }
</script>

<style scoped lang="scss">
  .group-card {
    border-radius: var(--border-radius);
    transition: all 0.2s ease-in-out;
    cursor: pointer;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;

    &--interactive {
      &:hover {
        transform: translateY(-2px);
        box-shadow: var(--shadow-focus);
      }
    }

    &--synced {
      background: linear-gradient(to bottom right, white, var(--sm-primary-05));
    }

    :deep(.el-card__body) {
      display: flex;
      flex-direction: column;
      flex: 1;
      padding: 0.75rem; // Reduced padding for compact design
      gap: 0.5rem;
      height: 100%;
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 0.5rem;
    }

    &__header-content {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex: 1;
      min-width: 0;
      flex-wrap: wrap;
    }

    &__title {
      font-size: 1rem;
      font-weight: 600;
      margin: 0;
      color: var(--sm-contrast);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 100%;
    }

    &__badge {
      flex-shrink: 0;
      height: 20px;
      line-height: 18px;
      padding: 0 6px;
      font-size: 0.75rem;

      :deep(.el-tag__content) {
        display: inline-flex;
        align-items: center;
      }
    }

    &__badge-content {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      white-space: nowrap;
    }

    &__header-actions {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      opacity: 0;
      transition: opacity 0.2s;
    }

    &:hover &__header-actions,
    &__header-actions:focus-within {
      opacity: 1;
    }

    &__icon-action {
      padding: 4px;
      min-width: 28px;
      height: 28px;
      border-radius: 6px;

      &:hover {
        transform: scale(1.05);
      }
    }

    &__menu-trigger {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 6px;
      color: var(--sm-contrast-muted);
      cursor: pointer;
      transition: all 0.2s ease-in-out;

      &:hover {
        background-color: var(--sm-gray-100);
        color: var(--sm-contrast);
      }
    }

    &__body {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    &__stats {
      display: flex;
      gap: 1rem;
      flex-wrap: wrap;
    }

    &__stat {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      font-size: 0.8125rem;
      color: var(--sm-contrast-muted);
    }

    &__stat-value {
      font-weight: 600;
      color: var(--sm-contrast);
    }

    &__stat-label {
      color: var(--sm-contrast-muted);
    }

    &__apps {
      display: flex;
      align-items: center;
      gap: 0;
      min-height: 32px;
      padding-left: 4px;
    }

    &__app-icon {
      width: 32px;
      min-width: 32px;
      height: 32px;
      border-radius: 50%;
      background: white;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: var(--shadow-light);
      // Transition handled in inline style for coordination
      border: 2px solid white;

      &:hover {
        box-shadow: var(--shadow);
      }
    }

    &__app-overflow {
      width: 32px;
      min-width: 32px;
      height: 32px;
      border-radius: 50%;
      background: var(--sm-gray-100);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--sm-contrast-muted);
      border: 2px solid white;
      cursor: default;
    }

    &__no-apps {
      padding: 0.25rem 0;
      font-style: italic;
      font-size: 0.8125rem;
    }
  }

  // Dark mode adjustments
  html.dark .group-card {
    &__app-icon {
      background: var(--sm-elevation);
      border-color: var(--sm-card-bg);
    }

    &__app-overflow {
      background: var(--sm-gray-700);
      border-color: var(--sm-card-bg);
    }

    &__menu-trigger {
      &:hover {
        background-color: var(--sm-gray-700);
      }
    }

    &--synced {
      background: linear-gradient(
        to bottom right,
        var(--sm-card-bg),
        rgba(0, 79, 159, 0.1)
      );
    }
  }
</style>
